import { useState } from 'react'
import { Check, Copy, Loader2, RefreshCw, Sparkles, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { draftEvaluation, type EvaluationDraft, type EvaluationDraftKind, type EvaluationDraftSources } from 'services/aiService'

import { AskMdtMarkdown } from './AskMdtMarkdown'

const LABELS: Record<EvaluationDraftKind, { button: string; title: string; blurb: string }> = {
	comparison: {
		button: 'Draft comparison',
		title: 'Site comparison draft',
		blurb: 'A written comparison of the sites you chose, from their scores, financials, risks and pros and cons.',
	},
	memo: {
		button: 'Draft approval memo',
		title: 'Approval memo draft',
		blurb: 'A recommendation memo for your approvers, from this evaluation’s scores, financials, risks and readiness.',
	},
}

const FINANCIALS_WORDS: Record<EvaluationDraftSources['financials'], string> = {
	included: 'financials',
	'none entered': 'no financials entered yet',
	'not on this plan': 'financials not included on your plan',
}

/** "Based on: Gilroy Pad, Hollister Lot · scores · financials · 3 risks · 4 pros and cons" */
export const basedOn = (sources: EvaluationDraftSources): string =>
	[
		sources.sites.join(', '),
		sources.scoring ? 'scores' : 'no scores yet',
		FINANCIALS_WORDS[sources.financials],
		`${sources.risks} risk${sources.risks === 1 ? '' : 's'}`,
		`${sources.prosAndCons} pros and cons`,
	].join(' · ')

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't write a draft just now. Try again in a moment."

/**
 * "Draft with AI" for an evaluation (MYD-639): a comparison of the chosen
 * sites, or an approval memo, written from the evaluation's own data on the
 * server and shown here to read, copy and regenerate. Saved nowhere: the user
 * takes what they want of it.
 *
 * Says what it was based on, and that it is an AI draft whose figures want
 * checking, because a memo goes to approvers.
 */
export function EvaluationDraftPanel({
	evaluationId,
	kind,
	siteIds,
	draft = draftEvaluation,
}: {
	evaluationId: string
	kind: EvaluationDraftKind
	siteIds?: string[]
	draft?: typeof draftEvaluation
}) {
	const labels = LABELS[kind]
	const [result, setResult] = useState<EvaluationDraft | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [copied, setCopied] = useState(false)

	const write = async () => {
		setBusy(true)
		setError(null)
		setCopied(false)
		try {
			setResult(await draft(evaluationId, { kind, ...(siteIds && { siteIds }) }))
		} catch (failure) {
			setError(messageOf(failure))
		} finally {
			setBusy(false)
		}
	}

	const copy = async () => {
		if (!result) return
		try {
			await navigator.clipboard.writeText(result.text)
			setCopied(true)
		} catch {
			setError('Copy did not work here: select the text and copy it instead.')
		}
	}

	if (!result) {
		return (
			<div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3">
				<Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
				<p className="min-w-0 flex-1 text-sm text-gray-600">{labels.blurb}</p>
				<Button type="button" variant="ghost" onClick={write} disabled={busy}>
					{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />}
					{busy ? 'Drafting…' : labels.button}
				</Button>
				{error && (
					<p role="alert" className="w-full text-xs text-destructive">
						{error}
					</p>
				)}
			</div>
		)
	}

	return (
		<section aria-label={labels.title} className="rounded-lg border border-gray-200 bg-white p-4">
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div>
					<h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
						<Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
						{labels.title}
					</h4>
					<p className="mt-0.5 text-xs text-gray-500">AI draft: check the figures before you use it. Based on {basedOn(result.sources)}.</p>
				</div>
				<div className="flex items-center gap-1">
					<Button type="button" variant="ghost" size="input" onClick={copy}>
						{copied ? <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
						{copied ? 'Copied' : 'Copy'}
					</Button>
					<Button type="button" variant="ghost" size="input" onClick={write} disabled={busy}>
						{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
						Regenerate
					</Button>
					<Button type="button" variant="ghost" size="input" onClick={() => setResult(null)} aria-label={`Close the ${labels.title.toLowerCase()}`}>
						<X className="h-3.5 w-3.5" aria-hidden="true" />
					</Button>
				</div>
			</div>
			{error && (
				<p role="alert" className="mt-2 text-xs text-destructive">
					{error}
				</p>
			)}
			<AskMdtMarkdown text={result.text} className="mt-3 text-sm text-gray-800" />
		</section>
	)
}

export default EvaluationDraftPanel
